//#region Util Imports

import * as _ from 'underscore'

//#endregion


//#region Asset Imports

import DevExpress from 'devextreme'

//#endregion

import { DXDashboardService, DXDashboardFilter } from '../../services'
import { LogHelper, LocalizationHelper, TypeHelper, TemplateHelper, ConfigHelper } from '../../../../helpers'


// Public Types

export interface IIMRDXDashboardFilterComponent {
    currentFilterName: string

    refresh(): void
}

export type IMRDXDashboardFilterOptions = {
    dashboardId: number

    onFilterAdded?(name: string): void
    onFilterChanged?(filter: DXDashboardFilter): void
    onInitialized?(filterComponent: IIMRDXDashboardFilterComponent): void
}

// Public Types End


export class IMRDXDashboardFilterController implements ng.IComponentController, IIMRDXDashboardFilterComponent {

    // Consts

    private static get LOG_PREFIX(): string { return '[SIMAX.Module.Dashboard.DXDashboardFilter]' }

    // Consts End


    // Members

    private options: IMRDXDashboardFilterOptions

    private filterOptionsPopup: DevExpress.ui.dxPopup
    private filterSelectBox: DevExpress.ui.dxSelectBox
    private form: DevExpress.ui.dxForm
    private dashboardId: number

    private filterSelectBoxOptions: DevExpress.ui.dxSelectBoxOptions
    private filterAddButtonOptions: DevExpress.ui.dxButtonOptions
    private filterRemoveButtonOptions: DevExpress.ui.dxButtonOptions
    private filterOptionsPopupOptions: DevExpress.ui.dxPopupOptions
    private filterOptionsFormOptions: DevExpress.ui.dxFormOptions
    private filterOptionsButtonOptions: DevExpress.ui.dxButtonOptions

    // Members End


    // Ctor

    constructor(private settingsService: DXDashboardService) { }

    // Ctor End


    // IDXDashboardFilter Implementation

    public get currentFilterName(): string { return this.getFilterSelectedFilterName() }
    public set currentFilterName(value: string) { this.selectFilter(value) }

    public refresh(): void {
        this.refreshSelectBox()
    }

    // IDXDashboardFilter Implementation End


    // IComponentController Implementation 

    public $onInit(): void {
        this.setOptions()

        $
            .when(
                this.initializeFilter(),
                this.initializeFilterSelectBoxOptions(),
                this.initializeFilterButtonOptions()
            )
            .then(() => {
                this.onInitialized()
            })
            .fail(() => {
                LogHelper.warn(IMRDXDashboardFilterController.LOG_PREFIX, `Unable to initialize.`)
            })
    }

    // IComponentController Implementation End


    // Events

    private onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }

    private onFilterAdded(): void {
        if (this.options != null && _.isFunction(this.options.onFilterAdded)) {
            const validationResult: any = this.form.validate()

            if (validationResult == null || validationResult.isValid) {
                const name: string = (this.form.getEditor('name') as any).option('value')

                this.options.onFilterAdded(name)
                this.filterOptionsPopup.hide()
            }
        }
    }

    private onFilterChanged(value: any): void {
        if (this.options != null && _.isFunction(this.options.onFilterChanged)) {
            this.options.onFilterChanged(this.getFilterByName(value))
        }
    }

    // Events End


    // Select Box

    private getFilterSelectedFilterName(): string {
        return this.filterSelectBox.option('value')
    }

    private selectFilter(filterName: string): void {
        if (this.filterSelectBox != null) {
            this.filterSelectBox.option('value', filterName)
        }
    }

    private refreshSelectBox(): void {
        this.filterSelectBox.option('dataSource', this.settingsService.getCurrentFilters(this.dashboardId))
    }

    private initializeFilterSelectBoxOptions(): JQueryPromise<any> {
        this.filterSelectBoxOptions = {
            dataSource: this.settingsService.getCurrentFilters(this.dashboardId),
            displayExpr: 'name',
            placeholder: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Filter),
            valueExpr: 'name',

            onInitialized: (e: any) => {
                this.filterSelectBox = e.component
                // this.selectFilter(this.settingsService.getCurrentFilterName(this.dashboardId))
            },

            onValueChanged: (e: any) => this.onFilterChanged(e.value)
        }

        return TypeHelper.onInitializedAll(this.filterSelectBoxOptions)
    }

    // Select Box End


    // Buttons

    private onFilterAddButtonClick(): void {
        if (this.filterOptionsPopup != null) {
            this.filterOptionsPopup.show()
        }
    }

    private onFilterRemoveButtonClick(): void {
        const filterName: any = this.filterSelectBox.option('value')

        this.settingsService.setCurrentFilters(this.dashboardId, this.settingsService.getCurrentFilters(this.dashboardId).filter(f => f.name !== filterName))

        if (this.settingsService.getCurrentFilterName(this.dashboardId) === filterName) {
            this.settingsService.setCurrentFilterName(this.dashboardId, null)
        }

        this.refreshSelectBox()
    }

    private initializeFilterButtonOptions(): JQueryPromise<any> {
        this.filterAddButtonOptions = {
            template: TemplateHelper.getIcon(ConfigHelper.Icon.ADD),

            onClick: () => this.onFilterAddButtonClick(),
        }

        this.filterRemoveButtonOptions = {
            template: TemplateHelper.getIcon(ConfigHelper.Icon.REMOVE),

            onClick: () => this.onFilterRemoveButtonClick(),
        }

        return TypeHelper.onInitializedAll(this.filterAddButtonOptions, this.filterRemoveButtonOptions)
    }

    // Buttons End


    // Filter Popup

    private initializeFilter(): JQueryPromise<any> {
        this.filterOptionsPopupOptions = {
            height: 'auto',
            maxWidth: '40rem',
            title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.FilterOptions),

            onInitialized: (e: any) => this.filterOptionsPopup = e.component
        }

        this.filterOptionsFormOptions = {
            items: [
                {
                    dataField: 'name',
                    isRequired: true,
                    label: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.FilterName)
                } as DevExpress.ui.dxFormSimpleItem
            ],

            onInitialized: (e: any) => this.form = e.component
        }

        this.filterOptionsButtonOptions = {
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Save),

            onClick: () => this.onFilterAdded()
        }

        return TypeHelper.onInitializedAll(this.filterOptionsPopupOptions)
    }

    // Filter Popup End


    private getFilterByName(filterName: string): DXDashboardFilter {
        return _.find(this.settingsService.getCurrentFilters(this.dashboardId), (filter: DXDashboardFilter) => filter.name === filterName) || null
    }

    // Options

    private setOptions(): void {
        if (this.options != null) {
            this.dashboardId = this.options.dashboardId
        }
    }

    // Options End
}
